/**
 *  This "Pricing" Component includes the front end logic and display for inventory pricing
 *
 **/
import PropTypes from 'prop-types';
import React from 'react';
import clsx from 'clsx';
import { Text, Button, InfoIcon } from 'reaxl';
import { formatCurrency, formatNumber } from '@atc/string-fns';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const renderMSRPWithLink = _ref => {
  var _span, _InfoIcon;
  let {
    context,
    formattedPrice,
    isFirstPrice,
    onClick,
    salePriceOnly = false,
    showCompact = false,
    showDeal = false,
    showDefault = false,
    showMSRPLabel = false,
    showPriceAndLink = true,
    showPayment,
    priceLabel = 'MSRP',
    priceDescription = ''
  } = _ref;
  const shouldDisplayMSRP = showDefault && (showMSRPLabel || salePriceOnly) || !showDefault;
  const showMSRPLabelPriceDifference = showPriceAndLink && !showDefault || showDeal && !isFirstPrice;
  const prefixClasses = clsx({
    'text-size-100 text-subdued display-block': !isFirstPrice && !showPayment,
    'padding-bottom-3': !['details', 'withDeal', 'withPayment', 'withDealIndicator'].includes(context),
    'padding-bottom-1': context === 'details',
    'display-flex align-items-baseline': !showMSRPLabelPriceDifference && showPayment
  }, 'text-normal', 'second-price');
  const msrpClassNames = clsx({
    'display-block': !showDeal,
    'padding-left-1': showDeal,
    'text-size-100': !showPayment,
    'text-size-200 margin-top-1': showPayment,
    'text-subdued': !showCompact
  });
  const viewPricingClassNames = clsx({
    'text-size-100': !showDefault,
    'text-size-200': showDefault
  }, 'display-block');
  const priceDescriptionClassNames = clsx('padding-left-1', {
    'text-size-100': !showPayment,
    'text-size-200': showPayment,
    'text-subdued': !showCompact
  });
  const getMsrpPriceTextWeight = () => {
    if (showMSRPLabelPriceDifference) {
      return undefined;
    }
    return context === 'withDealIndicator' ? 'ultra-bold' : 'bold';
  };
  const renderMsrpInfo = () => shouldDisplayMSRP && /*#__PURE__*/_jsxs(React.Fragment, {
    children: [showMSRPLabelPriceDifference && `${priceLabel} `, /*#__PURE__*/_jsx(Text, {
      weight: getMsrpPriceTextWeight(),
      children: formattedPrice
    }), !showPriceAndLink && !showDefault && (_span || (_span = /*#__PURE__*/_jsx("span", {
      className: msrpClassNames,
      children: priceLabel
    }))), priceLabel && priceDescription && (_InfoIcon || (_InfoIcon = /*#__PURE__*/_jsx(InfoIcon, {
      className: priceDescriptionClassNames,
      "data-cmp": "popover-pricing",
      infoText: priceDescription,
      popoverId: "price-description",
      placement: "top",
      textColor: "text-subdued"
    })))]
  }, "msrp");
  return /*#__PURE__*/_jsxs("span", {
    className: prefixClasses,
    children: [renderMsrpInfo(), showPriceAndLink && !['details', 'withDeal', 'withPayment', 'withDealIndicator'].includes(context) && /*#__PURE__*/_jsx(Button, {
      className: viewPricingClassNames,
      onClick: event => onClick(event),
      bsStyle: "text",
      children: "See Pricing Details"
    })]
  });
};

/**
 * data, uiContext, onClick
 */
const getPricingVariables = function (_ref2, context, onClick, showCompact, showDeal, showDefault, showPayment, priceLabel, priceDescription) {
  let {
    incentive,
    msrp,
    salePrice,
    noPriceLabel
  } = _ref2;
  if (priceLabel === void 0) {
    priceLabel = 'MSRP';
  }
  if (priceDescription === void 0) {
    priceDescription = '';
  }
  let firstPrice;
  let secondPrice;
  let noPrice;
  let showPricingDetailsCTA = false;
  const salePriceValue = incentive || salePrice;
  const salePriceOnly = salePriceValue && !msrp;
  const msrpOnly = msrp && msrp !== 0 && !salePriceValue;
  const saleAndMSRPNotEqual = salePriceValue && msrp && salePriceValue !== msrp;
  const saleAndMSRPEqual = salePriceValue && msrp && salePriceValue === msrp;
  const priceDifference = salePriceValue && msrp && msrp - salePriceValue;
  const hasDeal = priceDifference > 0;
  const showMSRPLabel = msrpOnly || saleAndMSRPEqual;
  const style = showPayment || showCompact ? 'currency' : '';
  let priceClassName = clsx('text-bold', {
    'text-gray-base': !showDefault && !showCompact,
    'text-size-300 text-blue-darker text-link': showDefault && !showPayment,
    'text-size-400': showCompact,
    'text-size-500': ['listing', 'snapshot', 'withDealIndicator'].includes(context),
    'text-size-600': ['details', 'withDeal', 'withPayment'].includes(context)
  });
  if (salePriceOnly) {
    firstPrice = formatNumber({
      value: salePriceValue,
      style
    });
  } else if (showMSRPLabel) {
    firstPrice = formatNumber({
      value: msrp,
      style
    });
    showPricingDetailsCTA = true;
  } else if (saleAndMSRPNotEqual) {
    firstPrice = formatNumber({
      value: salePriceValue,
      style
    });
    secondPrice = formatCurrency(msrp);
    showPricingDetailsCTA = true;
  } else {
    firstPrice = noPriceLabel;
    priceClassName = showPayment ? 'text-size-400' : 'text-size-300';
    noPrice = true;
  }
  firstPrice = showPricingDetailsCTA && showMSRPLabel || showDefault && salePriceOnly ? renderMSRPWithLink({
    context,
    formattedPrice: firstPrice,
    isFirstPrice: true,
    onClick,
    showPriceAndLink: showDefault,
    salePriceOnly,
    showCompact,
    showDeal,
    showDefault,
    showMSRPLabel,
    showPayment,
    priceLabel,
    priceDescription
  }) : firstPrice;
  secondPrice = secondPrice && showPricingDetailsCTA && renderMSRPWithLink({
    context,
    formattedPrice: secondPrice,
    isFirstPrice: false,
    onClick,
    showCompact,
    showDeal,
    showDefault,
    showMSRPLabel,
    showPayment,
    priceLabel,
    priceDescription
  });
  secondPrice = ['default', 'listing', 'details', 'withDeal', 'withPayment', 'withDealIndicator'].includes(context) ? secondPrice : null;
  return {
    firstPrice,
    secondPrice,
    priceClassName,
    priceDifference: hasDeal && formatCurrency(priceDifference),
    noPrice
  };
};
function Pricing(_ref3) {
  var _React$Fragment;
  let {
    className,
    data = {},
    isPreorder = false,
    // TODO convert to uiContext
    onPricingDetailClick = () => {},
    orientation,
    uiContext = 'listing',
    walletCTA,
    showSavings,
    priceLabel = 'MSRP',
    priceDescription = '',
    ...rest
  } = _ref3;
  let showDeal = uiContext === 'withDeal';
  let showDefault = uiContext === 'default';
  const showCompact = uiContext === 'compact';
  const showPayment = ['withPayment', 'withDealIndicator'].includes(uiContext);
  const showPriceDifference = !!showSavings || !!showDeal;
  if (showPayment || showCompact) {
    showDeal = true;
    showDefault = false;
  }
  const {
    firstPrice,
    secondPrice,
    priceClassName,
    priceDifference,
    noPrice
  } = getPricingVariables(data, uiContext, onPricingDetailClick, showCompact, showDeal, showDefault, showPayment, priceLabel, priceDescription);
  const rootClasses = clsx(priceClassName, className, {
    'display-flex align-items-center': showDeal || showPayment || showCompact
  });
  const getPriceLabelWithExpansion = label => {
    const expansions = {
      TSRP: '(Total Suggested Retail Price)'
    };
    return `${label} ${expansions[label] || ''}`.trim();
  };
  const tooltip = `${getPriceLabelWithExpansion(priceLabel)} excludes tax, title, license, delivery, processing & handling fees and optional equipment. Contact dealer for details.`;
  const renderPriceDifference = () => {
    const savedPrice = showDeal && !!secondPrice && /*#__PURE__*/_jsxs("div", {
      className: clsx('padding-left-3', {
        'display-block': !showPayment,
        'display-flex align-item-center': showPayment
      }),
      children: [/*#__PURE__*/_jsx(Text, {
        className: clsx('second-price', {
          'display-block': !showPayment,
          'margin-right-2': showPayment
        }),
        color: "subdued-lightest",
        size: 200,
        weight: "normal",
        children: secondPrice
      }), showPriceDifference && !!priceDifference && /*#__PURE__*/_jsx(Text, {
        className: clsx({
          'display-block': !showPayment
        }),
        color: "success",
        size: 200,
        weight: "light",
        children: `Save ${priceDifference}`
      })]
    });
    return !!walletCTA || showDeal && !!secondPrice ? /*#__PURE__*/_jsxs("span", {
      className: clsx({
        'display-flex flex-column': showPayment
      }),
      children: [savedPrice, !!walletCTA && !noPrice && (_React$Fragment || (_React$Fragment = /*#__PURE__*/_jsx(React.Fragment, {
        children: walletCTA
      }, "wallet-cta")))]
    }) : null;
  };
  return /*#__PURE__*/_jsxs("div", {
    "data-cmp": "pricing",
    className: rootClasses,
    ...rest,
    children: [/*#__PURE__*/_jsx("span", {
      className: clsx({
        'first-price': !showDeal && !noPrice && !showPayment && !showCompact,
        'first-price first-price-lg': showDeal && !noPrice && !showPayment && !showCompact,
        'text-size-700': uiContext === 'withDeal' && !noPrice,
        'text-size-600': showPayment && !noPrice && uiContext !== 'withDealIndicator',
        'padding-bottom-1': noPrice && showPayment
      }, 'text-ultra-bold'),
      "data-cmp": "firstPrice",
      children: firstPrice
    }), !isPreorder && /*#__PURE__*/_jsxs(_Fragment, {
      children: [!showDeal && secondPrice, renderPriceDifference()]
    }), isPreorder && !showDeal && /*#__PURE__*/_jsxs("span", {
      className: "text-size-200 text-subdued-lightest display-block padding-bottom-3 text-normal second-price",
      children: [/*#__PURE__*/_jsx(InfoIcon, {
        popoverId: "msrp-more-info-popover",
        placement: "top",
        infoText: tooltip
      }), "Starting", ' ', priceLabel, ' ', /*#__PURE__*/_jsx("span", {
        className: clsx({
          'display-block': orientation === 'landscape'
        }),
        children: "before options"
      })]
    })]
  });
}
export default Pricing;